import * as React from "react";
import { useState } from "react";
import { isToday, isSameDay } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";
import { Calendar } from "@/components/ui/Calendar";
import { formatDate } from "@/lib/formatDate";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import { useLocale } from "@/hooks/useLocale";
import { DayPickerProps } from "react-day-picker";

type DateInputProps = DayPickerProps & {
  value: Date | undefined;
  onUpdate?: (date: Date) => void;
  placeholder?: string;
  dateFormat?: string;
  widthFull?: boolean;
  timeZone?: string;
  showApplyButton?: boolean;
  buttonClassName?: string;
  onApply?: (date: Date) => void;
};

const DEFAULT_DATE_FORMAT = "PPP";

export function DateInput({
  value,
  onUpdate,
  placeholder = "Pick a date",
  dateFormat = DEFAULT_DATE_FORMAT,
  widthFull = true,
  timeZone,
  disabled,
  defaultMonth,
  showApplyButton = false,
  buttonClassName,
  onApply,
}: DateInputProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(value);

  const label = value
    ? isToday(value)
      ? "Today"
      : formatDate(value, dateFormat, { locale })
    : placeholder;

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) return;

    if (showApplyButton) {
      setSelectedDate(date);
      if (onUpdate) {
        onUpdate(date);
      }
    } else {
      if (onUpdate) {
        onUpdate(date);
      }
      setIsOpen(false);
    }
  };

  const handleApply = () => {
    if (!selectedDate) return;

    if (!value || !isSameDay(selectedDate, value)) {
      if (onApply) {
        onApply(selectedDate);
      } else if (onUpdate) {
        onUpdate(selectedDate);
      }
    }

    setIsOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      setSelectedDate(value);
    }
  };

  const isApplyDisabled =
    !selectedDate || (value && selectedDate && isSameDay(selectedDate, value));

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant={"outline"}
          className={cn(
            "h-9.5 justify-start text-left font-normal",
            !value && "text-muted-foreground",
            widthFull ? "w-full" : "w-auto",
            buttonClassName,
          )}
          disabled={typeof disabled === "object" ? undefined : !!disabled}
        >
          <CalendarIcon className="h-4 w-4" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          data-testid="calendar"
          mode="single"
          selected={showApplyButton ? selectedDate : value}
          onSelect={handleDateSelect}
          defaultMonth={defaultMonth || value}
          timeZone={timeZone}
          disabled={disabled}
        />
        {showApplyButton && (
          <>
            <div className="w-full border-t border-border" />
            <div className="flex justify-end p-3">
              <Button
                size="sm"
                onClick={handleApply}
                disabled={isApplyDisabled}
                className="bg-button text-sm leading-5 font-medium text-white hover:bg-button/90 disabled:opacity-60"
              >
                {t("global.apply")}
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
